import {
  PLATFORM_SUBSCRIPTION_STATUS_LABELS,
  PLATFORM_SUBSCRIPTION_STATUSES,
  type PlatformTenantSummary,
  TENANT_STATUS_LABELS,
  tenantStatusSchema,
} from "@station/contracts";
import {
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Label,
  MoneyText,
  Select,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Building2, Plus } from "lucide-react";
import { type ReactElement, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateTenantDialog } from "../../../features/platform/tenant-dialogs.js";
import {
  usePlatformPermissions,
  usePlatformPlans,
  usePlatformTenants,
  type TenantFilters,
} from "../../../features/platform/queries.js";
import { PlatformPage, SubscriptionBadge, TenantStatusBadge } from "../../../features/platform/shared.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { useDebouncedValue } from "../../../lib/hooks.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";

export const Route = createFileRoute("/platform/kiracilar/")({ component: TenantsRoute });

/**
 * Kiracılar (Prompt 24 web): arama + durum / plan / abonelik / gecikme süzgeçleri; her satır ayrıntıya gider. Kiracı
 * oluşturma deneme aboneliği açar ve sahibi (hesabı yoksa davetle) bağlar.
 */
function TenantsRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePlatformPermissions();
  const plans = usePlatformPlans();
  const overdueId = useId();
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 300);
  const [status, setStatus] = useState<string | null>(null);
  const [planCode, setPlanCode] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [overdue, setOverdue] = useState(false);
  const [creating, setCreating] = useState(false);
  const filters: TenantFilters = { search: debounced, status, planCode, subscriptionStatus, overdue };
  const pages = useCursorPages(JSON.stringify(filters));
  const tenants = usePlatformTenants(filters, pages.cursor);

  const columns = useMemo<DataTableColumn<PlatformTenantSummary>[]>(
    () => [
      {
        id: "name",
        header: t("platform.fields.tenant"),
        cell: (row) => (
          <Link
            to="/platform/kiracilar/$id"
            params={{ id: row.id }}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {row.name}
          </Link>
        ),
      },
      {
        id: "status",
        header: t("platform.fields.status"),
        cell: (row) => (
          <span className="flex flex-wrap gap-1">
            <TenantStatusBadge status={row.status} />
            {row.readOnlyReason === "BILLING" ? (
              <StatusBadge tone="danger">{t("platform.tenants.billingLock")}</StatusBadge>
            ) : null}
          </span>
        ),
      },
      { id: "plan", header: t("platform.fields.plan"), cell: (row) => row.planName },
      {
        id: "subscription",
        header: t("platform.fields.subscription"),
        cell: (row) => <SubscriptionBadge status={row.subscriptionStatus} />,
      },
      {
        id: "stations",
        header: t("platform.fields.stations"),
        align: "right",
        cell: (row) => <span className="tabular">{formatCount(row.activeStations)}</span>,
      },
      {
        id: "members",
        header: t("platform.fields.members"),
        align: "right",
        cell: (row) => <span className="tabular">{formatCount(row.members)}</span>,
      },
      {
        id: "alarms",
        header: t("platform.fields.criticalAlarms"),
        align: "right",
        cell: (row) => (
          <span className={`tabular ${row.openCriticalAlarms > 0 ? "font-semibold text-destructive" : ""}`}>
            {formatCount(row.openCriticalAlarms)}
          </span>
        ),
      },
      {
        id: "overdue",
        header: t("platform.fields.overdueAmount"),
        align: "right",
        cell: (row) => <MoneyText value={row.overdueAmount} />,
      },
      {
        id: "activity",
        header: t("platform.fields.lastActivity"),
        cell: (row) => (
          <span className="whitespace-nowrap tabular">
            {row.lastActivityAt ? formatDateTime(row.lastActivityAt) : "—"}
          </span>
        ),
      },
    ],
    [t],
  );

  return (
    <PlatformPage
      title={t("platform.tenants.title")}
      description={t("platform.tenants.description")}
      permission="platform.tenants.read"
      actions={
        permissions.has("platform.tenants.manage") ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("platform.tenants.create")}
          </Button>
        ) : null
      }
    >
      <DataTable
        caption={t("platform.tenants.title")}
        columns={columns}
        rows={tenants.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={tenants.isPending}
        error={tenants.error ? errorMessage(tenants.error) : undefined}
        onRowClick={(row) => void navigate({ to: "/platform/kiracilar/$id", params: { id: row.id } })}
        emptyState={<EmptyState icon={<Building2 />} title={t("platform.tenants.empty")} />}
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: tenants.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(tenants.data?.page.nextCursor),
        }}
        filters={
          <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <FormField label={t("platform.fields.search")}>
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  value={search}
                  placeholder={t("platform.tenants.searchPlaceholder")}
                  onChange={(event) => setSearch(event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t("platform.fields.status")}>
              {(control) => (
                <Select
                  {...control}
                  value={status ?? ""}
                  onChange={(event) => setStatus(event.target.value || null)}
                >
                  <option value="">{t("platform.all")}</option>
                  {tenantStatusSchema.options.map((value) => (
                    <option key={value} value={value}>
                      {TENANT_STATUS_LABELS[value]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("platform.fields.plan")}>
              {(control) => (
                <Select
                  {...control}
                  value={planCode ?? ""}
                  onChange={(event) => setPlanCode(event.target.value || null)}
                >
                  <option value="">{t("platform.all")}</option>
                  {(plans.data ?? []).map((plan) => (
                    <option key={plan.code} value={plan.code}>
                      {plan.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("platform.fields.subscription")}>
              {(control) => (
                <Select
                  {...control}
                  value={subscriptionStatus ?? ""}
                  onChange={(event) => setSubscriptionStatus(event.target.value || null)}
                >
                  <option value="">{t("platform.all")}</option>
                  {PLATFORM_SUBSCRIPTION_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {PLATFORM_SUBSCRIPTION_STATUS_LABELS[value]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <div className="flex min-h-10 items-center gap-2">
              <Checkbox
                id={overdueId}
                checked={overdue}
                onChange={(event) => setOverdue(event.target.checked)}
              />
              <Label htmlFor={overdueId}>{t("platform.tenants.onlyOverdue")}</Label>
            </div>
          </div>
        }
      />
      {creating ? <CreateTenantDialog open={creating} onOpenChange={setCreating} /> : null}
    </PlatformPage>
  );
}
